import { FC, useEffect, useLayoutEffect, useRef, useState, type MouseEvent } from "react";
import { createPortal, flushSync } from "react-dom";
import { Link } from "react-router-dom";
import { cn } from "../../lib/cn";
import { ROUTES } from "../../model/routes";
import { siteAsset } from "../../model/siteAssets";
import type { SiteEvent } from "../../model/siteContent";

const AFISHA_DESKTOP_QUERY = "(min-width: 900px)";

type ChalkPlaybillProps = {
  items: SiteEvent[];
  loading: boolean;
  className?: string;
};

function useAfishaDesktop() {
  const read = () => window.matchMedia(AFISHA_DESKTOP_QUERY).matches;
  const [enabled, setEnabled] = useState(read);

  useEffect(() => {
    const desktop = window.matchMedia(AFISHA_DESKTOP_QUERY);
    const sync = () => setEnabled(desktop.matches);
    desktop.addEventListener("change", sync);
    return () => desktop.removeEventListener("change", sync);
  }, []);

  return enabled;
}

export const ChalkPlaybill: FC<ChalkPlaybillProps> = ({ items, loading, className }) => {
  const isEmpty = items.length === 0;
  const isDesktop = useAfishaDesktop();
  const [hovered, setHovered] = useState<SiteEvent | null>(null);
  const [pickedSlug, setPickedSlug] = useState<string | null>(null);
  const auroraRef = useRef<HTMLVideoElement | null>(null);
  const board = useAfishaPortal(isDesktop ? hovered : null);
  const sectionRef = useRef<HTMLElement>(null);
  const stage = sectionRef.current?.closest(".chalk-page__stage");
  const picked = items.find((item) => item.slug === pickedSlug && eventPosterPath(item));
  const mobileEvent = isDesktop ? null : (picked ?? items.find((item) => eventPosterPath(item)) ?? null);
  const portalEvent = isDesktop ? board.event : mobileEvent;

  const armAurora = () => {
    playAurora(auroraRef.current);
  };

  const pickMobileEvent = (slug: string) => {
    flushSync(() => setPickedSlug(slug));
    playAurora(auroraRef.current);
  };

  useEffect(() => {
    const aurora = auroraRef.current;
    if (!aurora) return;
    if (!portalEvent) {
      aurora.pause();
      return;
    }
    playAurora(aurora);
  }, [portalEvent]);

  return (
    <section ref={sectionRef} className={cn("chalk-playbill", className)} aria-label="Афиша спектаклей">
      {isDesktop && <AuroraSource videoRef={auroraRef} />}
      {loading ? (
        <p className="chalk-playbill__status" role="status">
          Загрузка афиши…
        </p>
      ) : isEmpty ? (
        <p className="chalk-playbill__status" role="status">
          Не удалось загрузить афишу. Проверьте соединение и обновите страницу.
        </p>
      ) : (
        <>
          {mobileEvent && (
            <AfishaPortal event={mobileEvent} shown inline auroraRef={auroraRef} onArm={armAurora} />
          )}
          <div className="chalk-playbill__list" role="list">
            {items.map((event) => (
              <PlaybillRow
                key={event.slug}
                event={event}
                selected={!isDesktop && mobileEvent?.slug === event.slug}
                onHover={
                  isDesktop
                    ? (value) => {
                        if (value) armAurora();
                        setHovered(value);
                      }
                    : undefined
                }
                onPick={isDesktop ? undefined : () => pickMobileEvent(event.slug)}
                onArm={armAurora}
              />
            ))}
          </div>
          <p className="chalk-playbill__hint">билеты — на странице спектакля</p>
        </>
      )}
      {isDesktop && board.event && stage instanceof HTMLElement &&
        createPortal(
          <div className="chalk-playbill__portal-slot">
            <AfishaPortal event={board.event} shown={board.shown} auroraRef={auroraRef} />
          </div>,
          stage,
        )}
    </section>
  );
};

function PlaybillRow({
  event,
  selected,
  onHover,
  onPick,
  onArm,
}: {
  event: SiteEvent;
  selected: boolean;
  onHover?: (event: SiteEvent | null) => void;
  onPick?: () => void;
  onArm?: () => void;
}) {
  const title = event.name?.trim() || "Спектакль";
  const dateLabel = event.soon
    ? "скоро"
    : event.date?.trim() || event.type?.trim() || "дата уточняется";
  const ageLabel = event.old?.trim();
  const isSoon = event.soon;
  const sideLabel = selected && !isSoon ? "перейти" : dateLabel;
  const rowClassName = cn(
    "chalk-playbill__row",
    isSoon && "chalk-playbill__row--soon",
    selected && "is-selected"
  );

  const content = (
    <>
      <div className="chalk-playbill__main">
        {ageLabel && <span className="chalk-playbill__badge">{ageLabel}</span>}
        <span className="chalk-playbill__title">{title}</span>
      </div>
      <span className="chalk-playbill__date">{sideLabel}</span>
    </>
  );

  const openPortal = (clickEvent: MouseEvent<HTMLElement>) => {
    if (!onPick || selected) {
      onArm?.();
      return;
    }
    clickEvent.preventDefault();
    onPick();
  };

  if (isSoon) {
    return (
      <div
        className={rowClassName}
        role="listitem"
        aria-label={`${title}, скоро`}
        aria-selected={selected}
        onClick={onPick ? openPortal : undefined}
        onPointerDown={onArm}
        onMouseEnter={onHover ? () => onHover(event) : undefined}
        onMouseLeave={onHover ? () => onHover(null) : undefined}
      >
        {content}
      </div>
    );
  }

  return (
    <Link
      to={`/${ROUTES.EVENTS}/${encodeURIComponent(event.slug)}`}
      className={rowClassName}
      role="listitem"
      aria-label={selected ? `${title}, перейти` : `${title}, ${dateLabel}`}
      aria-selected={selected}
      onClick={onPick ? openPortal : undefined}
      onPointerDown={onArm}
      onMouseEnter={onHover ? () => onHover(event) : undefined}
      onMouseLeave={onHover ? () => onHover(null) : undefined}
    >
      {content}
    </Link>
  );
}

const PORTAL_MS = 460;
const AURORA_SRC = "/brand/afisha-aurora.mp4";
const AURORA_POSTER = "/brand/afisha-aurora-poster.jpg";

function eventPosterPath(event: SiteEvent) {
  return event.cardImage?.trim() || event.listImage?.trim() || event.sideImage?.trim() || "";
}

function useAfishaPortal(hovered: SiteEvent | null) {
  const [event, setEvent] = useState<SiteEvent | null>(null);
  const [shown, setShown] = useState(false);
  const hasPoster = Boolean(hovered && eventPosterPath(hovered));

  useEffect(() => {
    if (hovered && hasPoster) {
      setEvent(hovered);
      let inner = 0;
      const outer = requestAnimationFrame(() => {
        inner = requestAnimationFrame(() => setShown(true));
      });
      return () => {
        cancelAnimationFrame(outer);
        cancelAnimationFrame(inner);
      };
    }
    setShown(false);
    const timer = window.setTimeout(() => setEvent(null), PORTAL_MS);
    return () => window.clearTimeout(timer);
  }, [hovered, hasPoster]);

  return { event, shown };
}

type AuroraRef = { current: HTMLVideoElement | null };

function playAurora(aurora: HTMLVideoElement | null) {
  if (!aurora) return;
  aurora.muted = true;
  const pending = aurora.play();
  if (pending) void pending.catch(() => {});
}

function paintAuroraFrame(
  context: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  aurora: HTMLVideoElement,
) {
  const pixelRatio = window.devicePixelRatio || 1;
  const width = Math.max(1, Math.round(canvas.offsetWidth * pixelRatio));
  const height = Math.max(1, Math.round(canvas.offsetHeight * pixelRatio));
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }
  const frameWidth = aurora.videoWidth;
  const frameHeight = aurora.videoHeight;
  if (!frameWidth || !frameHeight) return;
  const scale = Math.max(width / frameWidth, height / frameHeight);
  const drawWidth = frameWidth * scale;
  const drawHeight = frameHeight * scale;
  const offsetX = (width - drawWidth) / 2;
  const offsetY = (height - drawHeight) / 2;
  context.drawImage(aurora, offsetX, offsetY, drawWidth, drawHeight);
}

function AuroraSource({ videoRef }: { videoRef: AuroraRef }) {
  return createPortal(
    <video
      ref={videoRef}
      className="chalk-playbill__aurora-source"
      src={AURORA_SRC}
      poster={AURORA_POSTER}
      muted
      loop
      playsInline
      preload="auto"
      aria-hidden
    />,
    document.body,
  );
}

function AfishaPortal({
  event,
  shown,
  inline = false,
  auroraRef,
  onArm,
}: {
  event: SiteEvent;
  shown: boolean;
  inline?: boolean;
  auroraRef: AuroraRef;
  onArm?: () => void;
}) {
  const coverPath = eventPosterPath(event);
  const cover = coverPath ? siteAsset(coverPath) : "";
  const tint = colorToCss(event.colorBackground);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [fireLive, setFireLive] = useState(false);

  useLayoutEffect(() => {
    if (inline) return;
    const canvas = canvasRef.current;
    const aurora = auroraRef.current;
    if (!canvas || !aurora) return;
    const context = canvas.getContext("2d");
    if (!context) return;
    let frame = 0;
    const paint = () => {
      paintAuroraFrame(context, canvas, aurora);
      frame = requestAnimationFrame(paint);
    };
    paint();
    return () => cancelAnimationFrame(frame);
  }, [auroraRef, inline]);

  useEffect(() => {
    if (!tint || !shown) return;
    const scene = document.querySelector(".chalk-flip");
    const target = scene instanceof HTMLElement ? scene : document.documentElement;
    target.style.setProperty("--afisha-page-bg", tint);
    return () => {
      target.style.removeProperty("--afisha-page-bg");
    };
  }, [tint, shown]);

  if (!cover) return null;

  return (
    <div className={cn("chalk-playbill__portal", inline && "chalk-playbill__portal--inline", shown && "is-shown")}>
      <div className="chalk-playbill__portal-hole">
        <img className="chalk-playbill__portal-fire-poster" src={AURORA_POSTER} alt="" aria-hidden />
        {inline ? (
          <video
            ref={auroraRef}
            className={cn("chalk-playbill__portal-fire", fireLive && "is-live")}
            src={AURORA_SRC}
            poster={AURORA_POSTER}
            muted
            loop
            autoPlay
            playsInline
            preload="auto"
            aria-hidden
            onPointerDown={onArm}
            onCanPlay={onArm}
            onPlaying={() => setFireLive(true)}
          />
        ) : (
          <canvas ref={canvasRef} className="chalk-playbill__portal-fire" aria-hidden />
        )}
        <FadingPicture className="chalk-playbill__portal-img" src={cover} alt="" />
      </div>
    </div>
  );
}

function FadingPicture({
  src,
  alt,
  className,
}: {
  src: string;
  alt: string;
  className: string;
}) {
  const idRef = useRef(1);
  const [frames, setFrames] = useState<Array<{ src: string; id: number }>>(() => [{ src, id: 1 }]);
  const [frontId, setFrontId] = useState(1);
  const frontIdRef = useRef(frontId);
  frontIdRef.current = frontId;

  useEffect(() => {
    setFrames((current) => {
      const latest = current[current.length - 1];
      if (latest?.src === src) return current;
      idRef.current += 1;
      const next = { src, id: idRef.current };
      const shown = current.find((frame) => frame.id === frontIdRef.current) ?? latest;
      if (!shown || shown.src === src) return [next];
      return [shown, next];
    });
  }, [src]);

  useEffect(() => {
    const latest = frames[frames.length - 1];
    if (!latest || frames.length < 2) return;
    const frame = requestAnimationFrame(() => setFrontId(latest.id));
    const timer = window.setTimeout(() => {
      setFrames((current) => {
        const keep = current[current.length - 1];
        return keep ? [keep] : current;
      });
    }, PORTAL_MS);
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
    };
  }, [frames]);

  return (
    <>
      {frames.map((frame) => (
        <img
          key={frame.id}
          className={cn(className, frame.id === frontId && "is-front")}
          src={frame.src}
          alt={frame.id === frontId ? alt : ""}
        />
      ))}
    </>
  );
}

function colorToCss(value?: number) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "";
  const color = value & 0xffffff;
  const red = (color >> 16) & 255;
  const green = (color >> 8) & 255;
  const blue = color & 255;
  return `rgba(${red}, ${green}, ${blue}, 0.2)`;
}
